import { useCallback, useContext } from 'react'
import { Link } from '@/componentsV1/BaseComponents'
import {
  EndOfYearAdminReviewStep,
  EndOfYearReviewStepStatus,
  FinalReviewStep,
} from '../../types'
import BookkeepingReviewSection from '../bookkeeping-review-section'
import SimpleRow from '../simple-row'
import { TEST_IDS } from '../helpers'
import { AdminYearEndReviewContext } from '../context'
import UpdatedRow from '../updated-row'
import './styles.scss'

const { pending, userActionRequired, complete } = EndOfYearReviewStepStatus
const STEP_TYPE = EndOfYearAdminReviewStep.transactions

export type FinalReviewSectionSectionProps = {
  step: FinalReviewStep | null
  userId: number
}
const FinalReviewSection = ({
  step,
  userId,
}: FinalReviewSectionSectionProps) => {
  const { updateStep, openAdminNoteModal } = useContext(
    AdminYearEndReviewContext
  )

  const onConfirmClick = useCallback(() => {
    const status = step?.status === complete ? pending : complete
    updateStep({
      step: step?.step ?? STEP_TYPE,
      status,
    })
  }, [step, updateStep])

  const onNeedsClarificationCallback = useCallback(
    async (adminNote: string) => {
      const status =
        step?.status === userActionRequired ? pending : userActionRequired
      await updateStep({
        status,
        step: step?.step ?? STEP_TYPE,
        context: { ...step?.context, adminNote },
      })
    },
    [step, updateStep]
  )

  const resetToPending = useCallback(async () => {
    await updateStep({
      step: step?.step ?? STEP_TYPE,
      status: pending,
      context: { ...step?.context, adminNote: undefined },
    })
  }, [step, updateStep])

  return (
    <BookkeepingReviewSection
      sectionTitle="Final review of income and expenses"
      className="eoy-admin__final-review"
      status={step?.status ?? pending}
      confirmationTitle="Confirmed"
      minHeight={130}
      testId="final-review"
      onConfirmClick={onConfirmClick}
      updatedByUser
    >
      <UpdatedRow
        description="User has added notes to transactions"
        testId={TEST_IDS.finalReviewUpdatedRow}
      />

      <SimpleRow
        description="Check if any transactions need clarification"
        checkboxLabel="Needs clarification"
        checked={step?.status === userActionRequired}
        testId={TEST_IDS.checkIfTxNeedClarification}
        onCheckboxClick={() =>
          step?.status === userActionRequired
            ? resetToPending()
            : openAdminNoteModal(
                step?.context?.adminNote ?? null,
                onNeedsClarificationCallback
              )
        }
      />

      <Link
        className="eoy-admin__final-review__view-transactions"
        href={`/admin/finances/transactions/${userId}`}
        size="small"
        newPage
      >
        View new notes &rarr;
      </Link>
    </BookkeepingReviewSection>
  )
}

export default FinalReviewSection
